import { Box, Stack, Typography, useTheme } from '@mui/material';
import {
  GeneratedGroupType,
  PeriodTypeEnum,
  ScheduleStructure,
  useGetFacilitiesQuery,
  useGetStaffScheduleQuery,
  useGetStudentScheduleQuery,
  WeeklySchedule,
} from '@schooly/api';
import {
  ItemRoot,
  ItemTime,
  ItemTitle,
  Loading,
  ScheduleGrid,
  ScheduleGridItemData,
  ScheduleGridTooltip,
  ScheduleItemSize,
  SelectPopover,
  TagSimple,
  TagSubject,
  TooltipComponentProps,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/get-user-full-name';
import { FC, useCallback, useMemo } from 'react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { AccessDenied } from '@schooly/style';
import { PermissionGuard } from '../../../../components/Guards';
import { useProfile } from '../../../../context/profile/useProfile';
import { useSchool } from '../../../../hooks/useSchool';
import { TIMETABLE_ACCESS_PERMISSIONS } from '../../../../hooks/useTimetablePermissions';
import {
  TransformScheduleOptions,
  useWeeklyScheduleGrid,
} from '../../../../hooks/useWeeklyScheduleGrid';
import { ASSIGNABLE_PERIODS } from '../../../School/SchoolTimetables/CreateTimetable/TimetableSlotCard';
import { NoTimetablesStub } from '../../../School/SchoolTimetables/SchoolTimetables';
import { DROPDOWN, SCHEDULE } from '@schooly/constants/test-ids';

import { getPeriodTypePalette } from '../../../School/SchoolTimetableStructures/utils';

import { buildRoomMap, ScheduleGroupWithRoom, ScheduleItemData } from './utils';

const RegisterSessionTooltipChips: FC<{
  periodType: PeriodTypeEnum;
  session?: ScheduleItemData['registerSession'] | null;
}> = ({ periodType, session }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const showRegistrationChip = !!session;
  const showPeriodTypeChip = periodType !== PeriodTypeEnum.Registration;

  const registerPalette = getPeriodTypePalette(PeriodTypeEnum.Registration, theme);

  const renderTypeTag = (title: string, palette: ReturnType<typeof getPeriodTypePalette>) => {
    return (
      <TagSimple sx={{ px: 0.5, backgroundColor: palette.secondary }}>
        <Typography variant="h4" sx={{ textTransform: 'unset', color: palette.primary }}>
          {title}
        </Typography>
      </TagSimple>
    );
  };

  return (
    <Stack direction="row" gap={0.5} sx={{ mb: 0.5 }}>
      {showPeriodTypeChip &&
        renderTypeTag(
          formatMessage({ id: `school-timetableStructure-periodType-${periodType}` }),
          getPeriodTypePalette(periodType, theme),
        )}
      {showRegistrationChip &&
        renderTypeTag(
          formatMessage({ id: `school-timetableStructure-registerSession-${session}` }),
          registerPalette,
        )}
    </Stack>
  );
};

export const ScheduleEndAdornment: FC<{
  markers?: React.ReactNode;
  roomLabel?: string;
  staffLabel?: string | number;
  size: ScheduleItemSize;
}> = ({ markers, roomLabel, staffLabel, size }) => (
  <Stack alignItems="flex-end" sx={{ flexShrink: 0, maxWidth: '50px' }}>
    {markers}
    {(!markers || roomLabel) && (
      <ItemTime
        variant="caption"
        sx={{ maxWidth: '100%', height: '17px' }}
        data-test-id={SCHEDULE.ITEM_ROOM}
      >
        {roomLabel ?? ''}
      </ItemTime>
    )}
    {staffLabel && size === ScheduleItemSize.Full && (
      <ItemTime variant="caption">{staffLabel}</ItemTime>
    )}
  </Stack>
);

export function addScheduleEndAdornments(
  items: ScheduleGridItemData<ScheduleItemData>[],
): ScheduleGridItemData<ScheduleItemData>[] {
  return items.map((item) => {
    const { roomLabel, staffLabel } = item.data;

    return !roomLabel && !staffLabel
      ? item
      : {
          ...item,
          endAdornment: ({ size }) => (
            <ScheduleEndAdornment roomLabel={roomLabel} staffLabel={staffLabel} size={size} />
          ),
        };
  });
}

type ProfileModalScheduleContentProps = {
  data: WeeklySchedule[] | undefined;
  isLoading: boolean;
  emptyMessage: string;
  transformOptions?: TransformScheduleOptions;
  roomMap: Map<string, string[]>;
};

const ProfileModalScheduleContent: FC<ProfileModalScheduleContentProps> = ({
  data,
  isLoading,
  emptyMessage,
  transformOptions,
  roomMap,
}) => {
  const navigate = useNavigate();

  const onGroupClick = useCallback(
    (groupId: string) => {
      navigate(`/groups/${groupId}`);
    },
    [navigate],
  );

  const {
    structures,
    selectedStructure,
    setSelectedStructureId,
    items,
    days,
    timeRange,
    hasSchedule,
  } = useWeeklyScheduleGrid({
    schedule: data,
    onLabelClick: onGroupClick,
    transformOptions,
    roomMap,
  });

  const itemsWithAdornments = useMemo(() => addScheduleEndAdornments(items), [items]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Stack sx={{ height: '100%' }}>
      <StructureSelect
        structures={structures}
        selectedStructure={selectedStructure}
        onSelect={setSelectedStructureId}
      />

      {hasSchedule ? (
        <Stack sx={{ flex: 1, width: '100%', height: '100%', pt: 2.5 }}>
          <ScheduleGrid<ScheduleItemData>
            days={days}
            timeRange={timeRange}
            items={itemsWithAdornments}
            TooltipComponent={ModalScheduleTooltip}
          />
        </Stack>
      ) : (
        <NoTimetablesStub label={emptyMessage} />
      )}
    </Stack>
  );
};

export const ProfileModalSchedule: FC = () => {
  const { schoolMembership } = useProfile();
  const studentRelationId = schoolMembership?.relation_id ?? '';
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useSchool();
  const { data: facilitiesData, isLoading: isFacilitiesLoading } = useGetFacilitiesQuery(
    { schoolId },
    { enabled: !!schoolId },
  );

  const { data, isLoading, isFetching } = useGetStudentScheduleQuery(
    { studentId: studentRelationId },
    { enabled: !!studentRelationId, refetchOnMount: 'always' },
  );

  const roomMap = useMemo(() => buildRoomMap(facilitiesData?.facilities ?? []), [facilitiesData]);

  return (
    <PermissionGuard permissions={TIMETABLE_ACCESS_PERMISSIONS} fallback={<AccessDenied />}>
      <ProfileModalScheduleContent
        data={data}
        isLoading={isLoading || isFetching || isFacilitiesLoading}
        emptyMessage={formatMessage({ id: 'profile-Timetable-NoActiveTimetable' })}
        roomMap={roomMap}
      />
    </PermissionGuard>
  );
};

export const ProfileModalScheduleStaff: FC = () => {
  const { schoolMembership } = useProfile();
  const staffRelationId = schoolMembership?.relation_id ?? '';
  const { formatMessage } = useIntl();
  const { schoolId = '' } = useSchool();
  const { data, isLoading, isFetching } = useGetStaffScheduleQuery(
    { staffId: staffRelationId },
    { enabled: !!staffRelationId, refetchOnMount: 'always' },
  );

  const { data: facilitiesData, isLoading: isFacilitiesLoading } = useGetFacilitiesQuery(
    { schoolId },
    { enabled: !!schoolId },
  );

  const roomMap = useMemo(() => buildRoomMap(facilitiesData?.facilities ?? []), [facilitiesData]);

  return (
    <PermissionGuard permissions={TIMETABLE_ACCESS_PERMISSIONS} fallback={<AccessDenied />}>
      <ProfileModalScheduleContent
        data={data}
        isLoading={isLoading || isFetching || isFacilitiesLoading}
        emptyMessage={formatMessage({ id: 'profile-Timetable-NoActiveTimetable-Staff' })}
        transformOptions={{ allowedPeriodTypes: ASSIGNABLE_PERIODS }}
        roomMap={roomMap}
      />
    </PermissionGuard>
  );
};

const ModalScheduleTooltip: FC<TooltipComponentProps<ScheduleItemData>> = ({ item, children }) => {
  const isAssignable = ASSIGNABLE_PERIODS.includes(item.data.periodType);
  const { periodType, registerSession, onGroupClick, groupsWithRoom } = item.data;
  const extraWidth = periodType === PeriodTypeEnum.Lesson && registerSession;

  return (
    <ScheduleGridTooltip
      width={extraWidth ? 260 : undefined}
      content={
        isAssignable ? (
          <TimeSlotTooltipContent
            startTime={item.startTime}
            endTime={item.endTime}
            groupsWithRoom={groupsWithRoom}
            onGroupClick={onGroupClick}
            periodType={periodType}
            session={registerSession}
          />
        ) : (
          <Stack>
            <Stack flexDirection="row" justifyContent="space-between" gap={1}>
              <Typography variant="caption" color="common.grey2">
                {item.startTime} - {item.endTime}
              </Typography>
              <RegisterSessionTooltipChips periodType={periodType} />
            </Stack>
            <Typography>{item.label}</Typography>
          </Stack>
        )
      }
    >
      {children}
    </ScheduleGridTooltip>
  );
};

export type TimeSlotTooltipContentProps = {
  startTime: string;
  endTime: string;
  groupsWithRoom: ScheduleGroupWithRoom[];
  onGroupClick?: (groupId: string) => void;
  periodType?: PeriodTypeEnum;
  session?: ScheduleItemData['registerSession'];
  renderGroupMarker?: (groupId: string) => React.ReactNode;
};

export const TimeSlotTooltipContent: FC<TimeSlotTooltipContentProps> = ({
  startTime,
  endTime,
  groupsWithRoom,
  onGroupClick,
  periodType,
  session,
  renderGroupMarker,
}) => {
  const { formatMessage } = useIntl();

  return (
    <ItemRoot sx={{ background: 'transparent' }}>
      <Stack flexDirection="row" justifyContent="space-between">
        <Typography variant="caption" color="common.grey2">
          {startTime} - {endTime}
        </Typography>
        {periodType && <RegisterSessionTooltipChips periodType={periodType} session={session} />}
      </Stack>
      <Stack gap={2}>
        {groupsWithRoom.map(({ id, name, staff_preview, room, type, subject }) => (
          <Stack key={id} gap={0.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
              <ItemTitle
                color="common.grey2"
                onClick={onGroupClick ? () => onGroupClick(id) : undefined}
                sx={{ whiteSpace: 'normal' }}
              >
                {name}
              </ItemTitle>
              {renderGroupMarker?.(id)}
            </Stack>

            <Box py={0.25}>
              {type === GeneratedGroupType.Value2 && (
                <TagSubject
                  renderLabel={() => (
                    <Typography variant="caption">
                      {formatMessage({ id: 'groups-TutorGroup' })}
                    </Typography>
                  )}
                  sx={{ height: 16 }}
                >
                  {formatMessage({ id: 'groups-TutorGroup' })}
                </TagSubject>
              )}
              {subject && (
                <TagSubject
                  sx={{ height: 16 }}
                  renderLabel={() => <Typography variant="caption">{subject.name}</Typography>}
                >
                  {subject.name}
                </TagSubject>
              )}
            </Box>
            <Typography
              variant="caption"
              color={!!staff_preview.length ? 'common.grey2' : 'common.grey'}
            >
              {!!staff_preview.length ? (
                <>
                  {formatMessage({ id: 'userType-staff' }) + ': '}
                  {staff_preview
                    .map((staff) =>
                      getUserFullName({
                        given_name: staff.given_name ?? '',
                        last_name: staff.last_name ?? '',
                        known_as: staff.known_as ?? null,
                      }),
                    )
                    .join(', ')}
                </>
              ) : (
                formatMessage({ id: 'groups-NoStaffYet' })
              )}
            </Typography>

            {room !== undefined && (
              <Typography variant="caption" color={room ? 'common.grey2' : 'common.grey'}>
                {room
                  ? `${formatMessage({ id: 'school-tabs-Facilities-Room' })}: ${room.join(
                      ' \u2022 ',
                    )}`
                  : formatMessage({ id: 'groups-NoRoom' })}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </ItemRoot>
  );
};

type StructureSelectProps = {
  structures: ScheduleStructure[];
  selectedStructure: ScheduleStructure | undefined;
  onSelect: (structureId: string) => void;
};

export const StructureSelect: FC<StructureSelectProps> = ({
  structures,
  selectedStructure,
  onSelect,
}) => {
  return (
    <Typography variant="h2">
      <Stack direction="row" gap={1} alignItems="center">
        <FormattedMessage id="school-sections-Timetable" />
        {structures.length > 1 ? (
          <SelectPopover
            options={structures}
            selectedOption={selectedStructure}
            onSelect={(structure) => onSelect(structure.id)}
            dataTestId={DROPDOWN.TIMETABLE_STRUCTURE}
          />
        ) : (
          <span>{selectedStructure?.name || ''}</span>
        )}
      </Stack>
    </Typography>
  );
};
